import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Meeting } from '@app/core/model/entities/meeting/meeting';
import { ManualWorkflowViewEnum } from '@app/core/model/entities/workflow/workflow';
import { AccessManager } from '@app/core/services/managers/access.manager';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { MeetingModalService } from '@app/features/main/views/meetings/meetings-inventory/modals/meeting-modal.service';
import { MeetingsService } from '@app/features/main/views/meetings/meetings.services';
import { TranslateService } from '@ngx-translate/core';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { filter, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'meeting-header',
    templateUrl: './meeting-header.component.html',
    styleUrls: ['./meeting-header.component.scss'],
    providers: [MeetingModalService],
    standalone: false
})
export class MeetingHeaderComponent implements OnInit {

  protected meeting: Meeting;
  protected readonly Permission = PermissionEnum;
  protected readonly ManualWorkflowViewEnum = ManualWorkflowViewEnum;
  protected readonly EntityType = EntityTypeEnum;

  private readonly destroyRef = inject(DestroyRef);
  private readonly appManager = inject(AppManager);
  private readonly titleService = inject(Title);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly meetingService = inject(MeetingsService);
  private readonly meetingModalService = inject(MeetingModalService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly snackbarManager = inject(SnackbarManager);
  protected readonly accessManager = inject(AccessManager);

  /**
   * Get current Meeting entity, initialize form fields and set page title.
   */
  public ngOnInit(): void {
    this.meeting = this.appManager.currentEntity as Meeting;

    // Update Meeting as it is updated outside the header
    this.meetingService.meetingUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updatedMeeting => this.meeting = updatedMeeting);

    this.activationEndService.pushOptionalParams({
      'TITLE.MEETING_IDENTIFIER': {
        identifier: this.meeting.identifier.toString()
      }
    });
    this.titleService.setTitle(`${this.translate.instant(
      'TITLE.MEETING_IDENTIFIER',
      {identifier: this.meeting.identifier}
    )} - myA`);
  }

  /**
   * Opens a confirmation dialog to delete the current Meeting.
   * If the User confirms, the Meeting is deleted and the user is redirected
   * to the Meetings page.
   */
  protected deleteMeeting(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SHEET,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.MEETING,
      this.meeting.id
    );

    this.meetingModalService.openDeleteMeetingsDialog([this.meeting]).pipe(
      filter(result => result),
      tap(() => this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.MEETING_DELETED'))),
      switchMap(() => this.router.navigate(
        ['organization', this.appManager.currentOrganization.id, 'meetings'],
        {replaceUrl: true}
      )),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe();
  }
}
